import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import {
    isFirebaseWebPushConfigured,
    registerDashboardPushToken,
    subscribeToForegroundMessages,
} from '../firebase/firebaseMessaging';
import { formatDateTime } from '../utils/dashboardTranslations';

function formatRelativeTime(dateString) {
    try {
        return formatDateTime(dateString);
    } catch {
        return dateString || '';
    }
}

function NotificationBell() {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const seenNotificationIdsRef = useRef(new Set());
    const pollRef = useRef(null);
    const audioContextRef = useRef(null);
    const notificationAudioRef = useRef(null);

    const getAudioContext = useCallback(() => {
        if (typeof window === 'undefined') {
            return null;
        }

        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) {
            return null;
        }

        if (!audioContextRef.current) {
            audioContextRef.current = new AudioContextClass();
        }

        return audioContextRef.current;
    }, []);

    const getNotificationAudio = useCallback(() => {
        if (typeof window === 'undefined' || typeof Audio === 'undefined') {
            return null;
        }

        if (!notificationAudioRef.current) {
            const audio = new Audio('/sounds/dashboard_notification.mp3');
            audio.preload = 'auto';
            notificationAudioRef.current = audio;
        }

        return notificationAudioRef.current;
    }, []);

    const unreadNotifications = useMemo(
        () => notifications.filter((notification) => !notification.isRead),
        [notifications]
    );

    const fetchNotifications = useCallback(async (limit = 12) => {
        setLoading(true);
        try {
            const response = await api.get('/notifications', { params: { limit } });
            setNotifications(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const showBrowserNotification = useCallback(async (notification) => {
        if (!('Notification' in window)) {
            return;
        }

        if (Notification.permission === 'default') {
            try {
                await Notification.requestPermission();
            } catch (error) {
                console.error('Failed to request notification permission:', error);
                return;
            }
        }

        if (Notification.permission !== 'granted') {
            return;
        }

        try {
            new Notification(notification.title, {
                body: notification.body,
                tag: `app-notification-${notification.id}`,
                icon: '/favicon.ico'
            });
        } catch (error) {
            console.error('Failed to show browser notification:', error);
        }
    }, []);

    const playFallbackNotificationTone = useCallback(async () => {
        const audioContext = getAudioContext();
        if (!audioContext) {
            return;
        }

        try {
            if (audioContext.state === 'suspended') {
                await audioContext.resume();
            }

            if (audioContext.state !== 'running') {
                return;
            }

            const now = audioContext.currentTime;
            const oscillator = audioContext.createOscillator();
            const gain = audioContext.createGain();

            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(880, now);
            oscillator.frequency.linearRampToValueAtTime(1174, now + 0.42);

            gain.gain.setValueAtTime(0.0001, now);
            gain.gain.exponentialRampToValueAtTime(0.12, now + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
            gain.gain.setValueAtTime(0.0001, now + 0.22);
            gain.gain.exponentialRampToValueAtTime(0.1, now + 0.26);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.42);

            oscillator.connect(gain);
            gain.connect(audioContext.destination);
            oscillator.start(now);
            oscillator.stop(now + 0.45);
        } catch (error) {
            console.error('Failed to play fallback notification tone:', error);
        }
    }, [getAudioContext]);

    const playNotificationSound = useCallback(async () => {
        const audio = getNotificationAudio();
        if (audio) {
            try {
                audio.pause();
                audio.currentTime = 0;
                await audio.play();
                return;
            } catch (error) {
                console.error('Failed to play dashboard notification audio, falling back to tone:', error);
            }
        }

        await playFallbackNotificationTone();
    }, [getNotificationAudio, playFallbackNotificationTone]);

    const refreshUnreadState = useCallback(async ({ initialLoad = false, suppressAlerts = false } = {}) => {
        try {
            const [countResponse, unreadResponse] = await Promise.all([
                api.get('/notifications/unread-count'),
                api.get('/notifications', { params: { unreadOnly: true, limit: 5 } })
            ]);

            const nextUnreadCount = Number(countResponse.data?.unreadCount || 0);
            const unreadItems = Array.isArray(unreadResponse.data) ? unreadResponse.data : [];
            setUnreadCount(nextUnreadCount);

            const newUnreadItems = unreadItems.filter((item) => !seenNotificationIdsRef.current.has(item.id));
            unreadItems.forEach((item) => seenNotificationIdsRef.current.add(item.id));

            if (!initialLoad && !suppressAlerts && newUnreadItems.length > 0) {
                showBrowserNotification(newUnreadItems[0]);
                playNotificationSound();
            }

            if (isOpen) {
                setNotifications((previous) => {
                    const byId = new Map(previous.map((item) => [item.id, item]));
                    unreadItems.forEach((item) => byId.set(item.id, item));
                    return Array.from(byId.values()).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                });
            }
        } catch (error) {
            console.error('Failed to refresh unread notifications:', error);
        }
    }, [isOpen, playNotificationSound, showBrowserNotification]);

    useEffect(() => {
        const unlockAudio = async () => {
            const audio = getNotificationAudio();
            if (audio) {
                try {
                    audio.muted = true;
                    audio.pause();
                    audio.currentTime = 0;
                    await audio.play();
                    audio.pause();
                    audio.currentTime = 0;
                } catch (error) {
                    console.error('Failed to unlock dashboard notification audio:', error);
                } finally {
                    audio.muted = false;
                }
            }

            const audioContext = getAudioContext();
            if (!audioContext || audioContext.state !== 'suspended') {
                return;
            }

            try {
                await audioContext.resume();
            } catch (error) {
                console.error('Failed to unlock notification sound:', error);
            }
        };

        window.addEventListener('pointerdown', unlockAudio, { passive: true });
        window.addEventListener('keydown', unlockAudio);

        return () => {
            window.removeEventListener('pointerdown', unlockAudio);
            window.removeEventListener('keydown', unlockAudio);
        };
    }, [getAudioContext, getNotificationAudio]);

    useEffect(() => {
        let unsubscribeForegroundMessages = () => {};

        const setupFirebaseMessaging = async () => {
            if (!isFirebaseWebPushConfigured()) {
                return;
            }

            try {
                await registerDashboardPushToken();

                unsubscribeForegroundMessages = await subscribeToForegroundMessages((payload) => {
                    const title = payload?.notification?.title;
                    const body = payload?.notification?.body;

                    if (title || body) {
                        showBrowserNotification({
                            id: payload?.messageId || Date.now(),
                            title: title || 'Wasel',
                            body: body || '',
                        });

                        playNotificationSound();
                    }

                    refreshUnreadState({ suppressAlerts: true });
                });
            } catch (error) {
                console.error('Failed to initialize Firebase web messaging:', error);
            }
        };

        setupFirebaseMessaging();

        return () => {
            unsubscribeForegroundMessages();
        };
    }, [playNotificationSound, refreshUnreadState, showBrowserNotification]);

    useEffect(() => {
        refreshUnreadState({ initialLoad: true });
        pollRef.current = setInterval(() => {
            refreshUnreadState();
        }, 10000);

        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
            }
        };
    }, [refreshUnreadState]);

    const handleToggle = async () => {
        const nextOpen = !isOpen;
        setIsOpen(nextOpen);
        if (nextOpen) {
            await fetchNotifications();
        }
    };

    const handleNotificationClick = async (notification) => {
        try {
            if (!notification.isRead) {
                await api.post(`/notifications/${notification.id}/read`);
                setNotifications((previous) => previous.map((item) => (
                    item.id === notification.id
                        ? { ...item, isRead: true }
                        : item
                )));
                setUnreadCount((previous) => Math.max(0, previous - 1));
            }
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
        }

        setIsOpen(false);
        if (notification.deepLink) {
            navigate(notification.deepLink);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await api.post('/notifications/read-all');
            setNotifications((previous) => previous.map((item) => ({ ...item, isRead: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Failed to mark all notifications as read:', error);
        }
    };

    const hasUnreadNotifications = unreadCount > 0;

    return (
        <div className="position-relative">
            <button
                type="button"
                className="btn border-0 d-flex align-items-center gap-3 position-relative"
                onClick={handleToggle}
                style={{
                    borderRadius: 22,
                    padding: '10px 16px 10px 12px',
                    minHeight: 58,
                    background: hasUnreadNotifications
                        ? 'linear-gradient(135deg, #0d6efd 0%, #4da3ff 100%)'
                        : 'linear-gradient(135deg, #ffffff 0%, #eef4ff 100%)',
                    color: hasUnreadNotifications ? '#ffffff' : '#163c74',
                    boxShadow: hasUnreadNotifications
                        ? '0 18px 35px rgba(13, 110, 253, 0.28)'
                        : '0 12px 24px rgba(15, 23, 42, 0.08)',
                    border: '1px solid rgba(13, 110, 253, 0.14)'
                }}
                aria-label="الإشعارات"
            >
                <span
                    className="d-inline-flex align-items-center justify-content-center"
                    style={{
                        width: 38,
                        height: 38,
                        borderRadius: 14,
                        background: hasUnreadNotifications ? 'rgba(255, 255, 255, 0.18)' : 'rgba(13, 110, 253, 0.12)'
                    }}
                >
                    <i className="fas fa-bell" style={{ fontSize: 16 }}></i>
                </span>

                <span className="text-end">
                    <span className="d-block fw-semibold" style={{ fontSize: '0.95rem', lineHeight: 1.1 }}>
                        الإشعارات
                    </span>
                    <span
                        className="d-block"
                        style={{
                            fontSize: '0.75rem',
                            lineHeight: 1.2,
                            color: hasUnreadNotifications ? 'rgba(255, 255, 255, 0.82)' : '#6b7b93'
                        }}
                    >
                        {hasUnreadNotifications ? 'يوجد تحديث جديد' : 'متابعة الطلبات'}
                    </span>
                </span>

                {hasUnreadNotifications && (
                    <span
                        className="badge rounded-pill ms-1"
                        style={{
                            minWidth: 30,
                            background: '#ffffff',
                            color: '#0d6efd',
                            fontWeight: 700,
                            fontSize: '0.78rem',
                            boxShadow: '0 8px 18px rgba(0, 0, 0, 0.14)'
                        }}
                    >
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div
                    className="card border-0 position-absolute end-0 mt-3"
                    style={{
                        width: 380,
                        maxWidth: '90vw',
                        zIndex: 2000,
                        borderRadius: 24,
                        overflow: 'hidden',
                        boxShadow: '0 24px 50px rgba(15, 23, 42, 0.18)'
                    }}
                >
                    <div
                        className="card-header border-0 d-flex align-items-center justify-content-between"
                        style={{
                            background: 'linear-gradient(135deg, #f8fbff 0%, #eef5ff 100%)',
                            padding: '18px 20px 16px'
                        }}
                    >
                        <div className="text-end">
                            <strong style={{ color: '#183b70' }}>الإشعارات</strong>
                            <div className="small mt-1" style={{ color: '#6b7b93' }}>
                                آخر التحديثات المهمة في الطلبات
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm"
                            onClick={handleMarkAllAsRead}
                            disabled={unreadCount === 0}
                            style={{
                                borderRadius: 999,
                                padding: '6px 12px',
                                background: unreadCount === 0 ? '#edf2f8' : '#ffffff',
                                color: unreadCount === 0 ? '#94a3b8' : '#0d6efd',
                                border: '1px solid rgba(13, 110, 253, 0.12)'
                            }}
                        >
                            تعليم الكل كمقروء
                        </button>
                    </div>

                    <div style={{ maxHeight: 420, overflowY: 'auto', backgroundColor: '#f7f9fc' }}>
                        {loading && (
                            <div className="p-3 text-center text-muted">جاري التحميل...</div>
                        )}

                        {!loading && notifications.length === 0 && (
                            <div className="p-4 text-center text-muted">
                                لا توجد إشعارات حتى الآن.
                            </div>
                        )}

                        {!loading && notifications.map((notification) => (
                            <button
                                key={notification.id}
                                type="button"
                                className="w-100 text-start border-0"
                                onClick={() => handleNotificationClick(notification)}
                                style={{
                                    padding: '16px 18px',
                                    opacity: notification.isRead ? 0.8 : 1,
                                    background: notification.isRead ? '#ffffff' : '#edf5ff',
                                    borderBottom: '1px solid rgba(15, 23, 42, 0.06)'
                                }}
                            >
                                <div className="d-flex justify-content-between align-items-start gap-3">
                                    <div>
                                        <div className="fw-bold text-dark">{notification.title}</div>
                                        <div className="small text-muted mt-1">{notification.body}</div>
                                        <div className="small text-secondary mt-2">
                                            {formatRelativeTime(notification.createdAt)}
                                        </div>
                                    </div>
                                    {!notification.isRead && (
                                        <span className="badge bg-primary">جديد</span>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>

                    {unreadNotifications.length > 0 && (
                        <div
                            className="card-footer border-0 small"
                            style={{
                                background: '#ffffff',
                                color: '#6b7b93',
                                padding: '14px 18px'
                            }}
                        >
                            لديك {unreadNotifications.length} إشعار غير مقروء.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default NotificationBell;
